import React from 'react'

import QRCode from 'qrcode.react'

import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import Heading from '@react/react-spectrum/Heading'
import Launch from '@react/react-spectrum/Icon/Launch'
import Link from '@react/react-spectrum/Link'

import { Toast } from '@react/react-spectrum/Toast'

import { PATHS } from 'config/constants'
import { getAliasedLink, useDialog } from 'hooks'
import A from 'services/analytics'

const TagManagerWarning = ({ projectId }) => (
  <Toast variant='warning' className='launch-warning'>
    No Tag Manager selected. Please visit{' '}
    <Link href={PATHS.PROJECT_DETAILS + projectId + PATHS.INTEGRATIONS_TAB} variant='quiet'>
      Integrations
    </Link>{' '}
    tab to choose Tag Manager and configure it.
  </Toast>
)

export function openClient(project, force = false) {
  A.triggerRunProject()
  const previewLink = getAliasedLink(project)
  const hasTagManager = project.integrations?.launch?.appId || project.integrations?.gtm?.containerId

  switch (project.meta.type) {
    case 'mobile':
      return true
    case 'mobile-v2':
      return true
    case 'web':
    case 'web-modular':
      if (hasTagManager || force) {
        window.open(previewLink)
        return false
      }
      return true
    case 'call-center':
    case 'customer-experience':
    case 'customer-experience-v2':
    case 'journey-trigger':
    case 'customer-hub':
    case 'web-social':
    case 'ssr':
      window.open(previewLink)
      return false
    case 'external-website':
      return true
    case 'link':
      window.open(previewLink)
      return false
    default:
      return false
  }
}

export function RunProjectDialog({ project, onClose, ...otherProps }) {
  const hasTagManager = project?.integrations?.launch?.appId || project?.integrations?.gtm?.containerId

  switch (project.meta.type) {
    case 'mobile':
    case 'mobile-v2':
      return (
        <Dialog
          title='Run project'
          confirmLabel='OK'
          backdropClickable={true}
          className='run-project-dialog'
          onConfirm={onClose}
          onCancel={onClose}
          {...otherProps}
        >
          {!hasTagManager && <TagManagerWarning projectId={project?.meta?.id} />}
          To run this project, simply:
          <ol>
            <li>
              Open{' '}
              <a href='/install' target='_blank' rel='noopener noreferrer'>
                {project.meta.type === 'mobile' ? 'DX Demo' : 'Live Demos'}
              </a>{' '}
              app on your mobile device
            </li>
            <li>Navigate to Settings screen</li>
            <li>Enter this Project ID or scan this QR code:</li>
          </ol>
          <div style={{ textAlign: 'center' }}>
            <Heading>{project?.meta?.id}</Heading>
            <QRCode value={project?.meta?.id} />
          </div>
        </Dialog>
      )
    case 'web':
    case 'web-modular':
      return (
        <Dialog
          title='Incomplete configuration'
          cancelLabel='Back'
          className='run-project-dialog'
          confirmLabel='Proceed anyway'
          backdropClickable={true}
          onConfirm={onClose}
          onCancel={onClose}
          {...otherProps}
        >
          {!hasTagManager && <TagManagerWarning projectId={project?.meta?.id} />}
        </Dialog>
      )
    case 'external-website':
      return (
        <Dialog
          title='External Website'
          confirmLabel='Close'
          className='run-project-dialog'
          backdropClickable={true}
          onConfirm={onClose}
          onCancel={onClose}
          {...otherProps}
        >
          This project ID should be referenced by Demo System Profile Viewer extension in your Launch property:
          <ul></ul>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <Heading>{project?.meta?.id}</Heading>
          </div>
          If you're running into an issue or don't know where to start:
          <ul>
            <li>
              <a href='https://docs.adobedemo.com/profile-viewer/overview' target='_blank' rel='noopener noreferrer'>
                Getting started with Profile Viewer
              </a>
            </li>
            <li>
              <a
                href='https://docs.adobedemo.com/profile-viewer/tips-and-tricks'
                target='_blank'
                rel='noopener noreferrer'
              >
                Troubleshooting common issues
              </a>
            </li>
          </ul>
        </Dialog>
      )
    case 'call-center':
    case 'journey-trigger':
    case 'customer-experience':
    case 'customer-hub':
    case 'web-social':
    default:
      return <></>
  }
}

export function RunProjectButton({ project }) {
  const { openDialog, closeDialog } = useDialog()
  const open = (props) => {
    if (openClient(project)) {
      const Dialog = <RunProjectDialog project={project} onClose={closeDialog} {...props} />
      openDialog(Dialog)
    }
  }

  switch (project?.meta?.type) {
    case 'mobile':
    case 'mobile-v2':
    case 'call-center':
    case 'journey-trigger':
    case 'customer-experience':
    case 'customer-experience-v2':
    case 'customer-hub':
    case 'web-social':
    case 'external-website':
      return (
        <Button variant='cta' icon={<Launch size={'XS'} />} onClick={() => open()}>
          Run
        </Button>
      )
    case 'web':
    case 'web-modular':
    case 'ssr':
      return (
        <Button
          variant='cta'
          icon={<Launch size={'XS'} />}
          onClick={() =>
            open({
              onConfirm: () => {
                openClient(project, true)
                closeDialog()
              },
            })
          }
        >
          Run
        </Button>
      )
    default:
      throw new Error(`Unknown project type: ${project?.meta?.type}`)
  }
}
